import gql from "graphql-tag";
import className from "classnames/bind";
import { useInView } from "motion/react";
import { AnimVals } from '@/utils/animUtils';
import { getReduceMotion } from '@/utils/reduceMotion';
import { useEffect, useMemo, useRef, useState } from "react";
import { Splide, SplideTrack } from "react-splide-ts";
import { CarouselRhtvFragment } from "@graphqlTypes";
import { CarouselPagination, Container, SectionHeader, RevealWrapper } from "@components";
import { ACFMediaFragment } from "@fragments";
import { useViewSize } from '@/providers/ViewSizeContext';
import Slide from "./CarouselRockHallTvSlide";
import { getChannelData } from "./utils";


import styles from "./CarouselRockHallTv.module.scss";
const cx = className.bind(styles);

type CarouselRockHallTvProps = CarouselRhtvFragment;

export default function CarouselRockHallTv(props: CarouselRockHallTvProps) {
  const { channels, headline, subhead, cta } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const rootEl = useRef<HTMLDivElement>(null);
  const splideRef = useRef<Splide>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canPlay, setCanPlay] = useState(false);
  const showHeader = Boolean(headline || subhead);
  const items = useMemo(() => {
    return channels.map(channel => getChannelData(channel)).filter(Boolean);
  }, [channels]);

  const isSingleItem = useMemo(() => items.length <= 1, [items]);

  // -------------------- Animate In

  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true;

  // -------------------- Other stuff

  const updateActiveIndex = () => {
    if (splideRef.current) {
      const { index } = splideRef.current.splide;
      setActiveIndex(index);
    }
  };

  useEffect(() => {
    if (!getReduceMotion()) {
      setCanPlay(isInView);
    }
  }, [isInView]);

  // -------------------- Splide

  const splideOptions = {
    type: 'slide',
    arrows: true,
    pagination: true,
    perPage: 1,
    perMove: 1,
    speed: 500,

    updateOnMove: true,

    // Desktop first (Splide default)
    fixedWidth: '100%',
    gap: '3rem',
    padding: {
      left: '5rem',
      right: '5rem',
    },

    // Responsive options (smaller than desktop)
    breakpoints: {
      // Below 1921px
      1921: {
        fixedWidth: isSingleItem ? '100%' : '91.5%',
        gap: '3rem',
        padding: {
          left: '5rem',
          right: '5rem',
        },
      },
      // Below 1025px
      1025: {
        padding: {
          left: '4rem',
          right: '4rem',
        },
        arrows: false
      },
      // Below 769px
      769: {
        fixedWidth: '100%',
        gap: '2rem',
        padding: {
          left: '2rem',
          right: '2rem',
        },
      },
    }
  };

  // Set specific accessibility attribute overrides dictated by the client
  useEffect(() => {
    if (!rootEl.current) return;
    const lists = rootEl.current.querySelectorAll('.splide__list');
    lists.forEach(list => {
      list.setAttribute('role', 'list');
    });
    const slides = rootEl.current.querySelectorAll('.splide__slide');
    slides.forEach(slide => {
      slide.setAttribute('role', 'listitem');
    });
  }, []);

  // -------------------- Render

  return (
    <section className={cx(["carousel-rock-hall-tv"])} ref={rootEl}>
    <RevealWrapper isInView={isInView}>

      {/* Headline */}
      { showHeader && (
        <Container>
          <SectionHeader
            headline={headline}
            subhead={subhead}
            isInView={isInView}
          />
        </Container>
      )}

      {/* Carousel */}
      <div>
        <Splide
          className={cx("splide")}
          ref={splideRef}
          options={splideOptions}
          onMoved={updateActiveIndex}
          hasTrack={false}
        >
          <SplideTrack>
            {items.map((channel, index) => {
              return (
                <Slide
                  canPlay={canPlay}
                  active={index === activeIndex}
                  key={index}
                  noBorder={items.every(item => !item.excerpt)}
                  channel={channel}
                  index={index}
                  isRevealEnabled={isRevealEnabled}
                  isInView={isInView}
                  {...channel}
                />
              );
            })}
          </SplideTrack>
          <Container className={cx("bottom")}>
            <CarouselPagination ref={containerRef} cta={cta} />
          </Container>
        </Splide>
      </div>

    </RevealWrapper>
    </section>
  );

}

CarouselRockHallTv.fragments = {
  entry: gql`
    ${ACFMediaFragment}
    fragment CarouselRHTV on FlexiblePageContentContentCarouselRhtvLayout {
      headline
      subhead
      cta {
        label
        linkType
        externalLink
        page {
          node {
            uri
          }
        }
      }
      channels {
        type
        labelOverride
        pillLabel
        channel {
          nodes {
            ... on RhtvChannel {
              title
              uri
              rhtvChannelContent {
                excerpt
                previewVideo
                thumbnail {
                  ...ACFMedia
                }
              }
            }
          }
        }
        manualSlide {
          headline
          excerpt
          mediaType
          videoUrl
          link {
            linkType
            externalLink
            sectionId
            page {
              node {
                uri
              }
            }
          }
          image {
            ...ACFMedia
          }
          videoThumbnail {
            ...ACFMedia
          }
        }
      }
    }
  `,
};
